<template>
	<div response question :id="`question-${questionData.number}`" @keyup.enter="submitQuestion">
		<questionheader :questionData="questionData" />

		<div data-question-main :data-columns="colAmt > 1 ? colAmt : false" :data-sr-type="srType">
			<component
				v-for="(answer, index) in questionData.answers"
				:key="`answer-${index}`"
				data-answer
                data-answer-roundbox
				:data-answer-checked="answer.isChecked"
				:data-no-radio="questionData.settings.hideRadioButtons"
				:data-has-no-image="answer.media.length === 0"
				:data-answer-type="answer.type"
				:is="`div-col-${colNumber(index)}`"
			>
				<!-- The checkbox or radio button used in the answer -->
				<div v-if="answer.type !== `Description`" data-answer-radio>
					<checkboxradio
						:questionData="questionData"
						:outerfield="false"
						:tabindex="srType !== `regular` ? -1 : 0"
						:answerData="answer"
						:type="questionData.type === `Single` ? `radio` : `checkbox`"
						:disabled="answer.isDisabled || !get.editingAllowed(questionData)"
						:name="`question-${questionData.id}`"
						@element-click="radiobuttonChanged($event, answer)"
					/>
				</div>
				<!-- Other parts of an answer that could be added like images, video or inputfields -->
				<div
					data-answer-input
					:data-image-with-answerdescription="(answer.media.length > 0 && answer.description !== ``) ? `` : false"
                    :data-has-image="(answer.media.length > 0 && answer.description === ``) ? `` : false"
					:data-media-position="answer.media.length > 0 ? answer.media[0].position : false"
					:data-coupled-to-radio="`answer-${answer.id}`"
					:disabled="answer.isDisabled || !get.editingAllowed(questionData)"
				>
					<media
						v-if="answer.media.length > 0"
						:url="`${state.surveyInfo.domain}/v1/${answer.media[0].url}`"
						:alt="answer.media[0].alt"
						:type="answer.media[0].mediaType"
						:tabindex="mediaTabindex(answer)"
						:disabled="answer.isDisabled || !get.editingAllowed(questionData) || state.global.showLoader"
						@element-clicked="mediaSelected($event, answer)"
						@element-focused="mediaSelected($event, answer)"
						@element-keyup="selectAnswer($event, answer)"
						:id="`question-${questionData.number}_answer-${answer.number}`"
						role="option"
					/>
					<questioninput
						v-if="
							(answer.media.length === 0 || answer.media[0].alignment !== `Over`) &&
							answer.type !== `Radiobutton` &&
							answer.type !== `Checkbox` &&
							answer.type !== `Description`
						"
						:answerData="answer"
						:questionData="questionData"
						:disabled="answer.isDisabled || !get.editingAllowed(questionData)"
                        response-input
						@element-receives-input="inputIsChanged(answer)"
						@element-focused="onInputFocus($event, answer)"
						:requiredShouldBeIgnored="!answer.isChecked"
						:labelCanBeFocused="questionData.settings.hideRadioButtons && srType !== `image-and-text`"
						:ref="`input-${answer.id}`"
						:showDescription="answer.description !== `` && (srType === `only-text` || srType === `image-and-text` || srType === `regular`)"
					/>
					<answerlabel
						v-if="
							(answer.media.length === 0 || answer.media[0].alignment !== `Over`) &&
							(answer.type == `Radiobutton` || answer.type === `Checkbox` || answer.type === `Description`)
						"
						:answerData="answer"
						:questionData="questionData"
						:showDescription="answer.description !== `` && (srType === `only-text` || srType === `image-and-text` || srType === `regular`)"
					/>
					<answerdescription v-if="answer.media.length !== 0 && answer.description !== `` &&  (srType === `regular` || srType === `only-images`) && answer.media[0].alignment === `Over`" :answerData="answer" :questionData="questionData" />
				</div>
			</component>
		</div>
		<questionfooter :questionData="questionData" />
		<validationpopup :questionValidation="questionValidation" />
	</div>
</template>

<script>
import mixins from "@/global/mixins";

export default {
	name: `response-question`,
	mixins: [mixins.defaultQuestion],
	data() {
		return {
			answerClickIsAllowed: true,
		};
	},
	computed: {
		colAmt: function () {
			return this.questionData.settings.desktopColumnCount;
		},
		// The amount of required rows. This is only used by the 'column' feature
		rowAmt: function () {
			return Math.ceil(this.questionData.answers.length / this.questionData.settings.desktopColumnCount);
		},
		answersGiven: function () {
			return this.questionData.answers.filter((answer) => answer.isChecked).length;
		},
        hasRequiredAnswers: function () {
            return this.questionData.answers.filter((answer) => answer.validation.required).length > 0;
        },
		// This is used to determine what kind of layout should be shown to the user based on all the settings and options used for this question
		srType: function () {
			if (!this.questionData.settings.hideRadioButtons) return `regular`;

			let allOptionsAreOnlyImages = this.questionData.answers.every((answer) => answer.media.length !== 0 && answer.media[0].alignment === `Over`);
			if (allOptionsAreOnlyImages) return `only-images`;

			let allOptionsAreLabels = this.questionData.answers.every(
				(answer) => answer.media.length === 0 && (answer.htmlInputType === `radio` || answer.htmlInputType === `checkbox`)
			);
			if (allOptionsAreLabels) return `only-text`;

			let allOptionsAreImagesWithLabels = this.questionData.answers.every((answer) => answer.media.length !== 0 && answer.media[0].alignment !== `Over`);
			if (allOptionsAreImagesWithLabels) return `image-and-text`;

			return `regular`;
		},
		// The point in which we should start putting all answers in their own column
		colBreakpoint: function () {
			const totalPossibleAnswers = this.colAmt * this.rowAmt;
			const totalAnswers = this.questionData.answers.length;
			const emptyAnswers = totalPossibleAnswers - totalAnswers;
			const emptyCols = emptyAnswers / (this.rowAmt - 1);

			return totalAnswers - emptyCols;
		},
	},
	methods: {
		// The column number of the answer. This is only used by the 'column' option
		colNumber: function (index) {
			// Should the answer be put on the first row?
			if (index >= this.colBreakpoint) {
				const previousAnswerColNumber = this.questionData.answers[index].colNumber;
				const newColNumber = previousAnswerColNumber + 1;

				this.questionData.answers[index].colNumber = newColNumber;

				return newColNumber;
			} else {
				const newColNumber = Math.floor(index / this.rowAmt) + 1;

				this.questionData.answers[index].colNumber = newColNumber;

				return newColNumber;
			}
		},
		// Determine whether the image should be allowed to be navigated by keyboard
		mediaTabindex: function (answer) {
			if (answer.isDisabled) return false;
			else if (this.srType === `only-images` || this.srType === `image-and-text`) return 0;
			else return false;
		},
		// Make sure that a selectbox or radio button is selected when something within that option has been clicked that can't
		// Don't touch this method unless you go through all layout types with both just the mouse and just the keyboard
		mediaSelected: function (e, answer) {
			let eventType = e.type;
			let isFocusedByTab = e.sourceCapabilities !== undefined;

			// If an image is clicked, we do want to reset the selection
			// Unless it is an MR, then we only want to be able to select with the mouse, not tabs
			if (e.target.localName === `img`) {
				if (this.questionData.type === `Single` && eventType === `click`) this.clickElement(answer.id);
				else if (this.questionData.type === `Multi` && eventType === `click`) this.clickElement(answer.id);
			} else {
				// If the answer is checked we don't want to reset this selection when the user reselects an input
				if (!answer.isChecked && answer.type !== `Description`) {
					if (eventType === `click` || isFocusedByTab || eventType === `input` || eventType === `focus`) this.clickElement(`answer-${answer.id}`);
				}
			}
		},
		inputIsChanged: function (answer) {
			if (answer.isChecked === false) this.clickElement(answer.id);
		},
		clickElement: function (id) {
			let answer = this.questionData.answers.find((answer) => answer.id === id);

			this.selectAnswer({ code: `Space` }, answer);
		},
		selectAnswer: function (e, answer) {
			if (this.questionData.type === `Single`) {
				this.questionData.answers.forEach((answer) => {
					answer.isChecked = false;
				});

				answer.isChecked = true;
			} else {
				answer.isChecked = !answer.isChecked;
			}

			// If an answer that is exclusive has been clicked, disable all remaining options
			if (answer.isExclusive) {
				this.checkForExclusiveAnswer(answer);
			}

			this.$store.dispatch(`autoNext`, {
				questionData: this.questionData,
			});
		},
		onInputFocus(e, answer){
			// This is for the feature 'Standaard antwoorden'. Only those fields have to be emptied when the user selects them
			if (answer.isEdited === undefined && answer.isDefaultAnswer) {
				answer.openField = "";
				answer.isEdited = true;
			}

			this.radiobuttonChanged(e, answer);
		},
		// Do things when the radio button changes it value
		radiobuttonChanged: function (e, answer) {
			// The only-text layout type has several events firing on a click. We only need one of them
			if (!this.answerClickIsAllowed) return;

			this.$store.dispatch(`autoNext`, {
				questionData: this.questionData,
			});

			this.answerClickIsAllowed = false;
			setTimeout(() => {
				this.answerClickIsAllowed = true;
			}, 100);

			if (this.questionData.type === `Multi`) {
				if(e.type === `click`) answer.isChecked = !answer.isChecked;
				else answer.isChecked = true;
			} else if (this.questionData.type === `Single`) {
				this.questionData.answers.forEach((answer) => {
					answer.isChecked = false;
				});

				answer.isChecked = true;
			}

			// Don't validate inputs that aren't selected
			this.questionData.answers.forEach((answer) => {
				let inputRef = this.$refs[`input-${answer.id}`];
				if (inputRef !== undefined) {
					let answerInput = inputRef[0].$el.getElementsByTagName("input")[0];
					if (answerInput !== undefined) {
						answerInput.setCustomValidity(``);
						answer.validationMessage = ``;
					}
				}
			});

			// If an answer that is exclusive has been clicked, disable all remaining options
			if (answer.isExclusive) {
				this.checkForExclusiveAnswer(answer);
			}
		},
		checkForExclusiveAnswer: function (answer) {
			this.questionData.answers
				.filter((a) => a.id !== answer.id)
				.forEach((a) => {
					if (answer.isChecked) {
						a.isChecked = false;
						a.isDisabled = true;
						a.openField = ``;
					} else {
						a.isDisabled = false;
					}
				});
		},
	},
	created() {
		// If there is an checked exclusive answer on page load we should disable the rest
		let checkedExclusiveAnswer = this.questionData.answers.find((answer) => answer.isChecked && answer.isExclusive);

		if (checkedExclusiveAnswer) {
			this.checkForExclusiveAnswer(checkedExclusiveAnswer);
		}
	},
};
</script>

<style scoped>
@import "./../styles/variables.css";

[data-sr-type="only-text"] {
	[data-answer] {
		transition: 0.5s;
		transition: 0.5s;
	}
}

[data-sr-type="only-images"] [data-answer] {
	width: auto;

	img {
		margin: 0 15px;
	}
}
[data-answer]:has([data-image-with-answerdescription]) {
    [data-answer-radio] > [data-checkboxradio] {
        padding: 22px 0 22px 20px;
    }
}
[data-answer-input][data-image-with-answerdescription] {
    align-items: center;
    flex-direction: row;
    img {
        margin-right: 5px;
        margin-left: 0;
    }
	[data-tooltip] {
        margin: 0;
	}
} 

[data-sr-type="image-and-text"] {
	[data-answer-input] {
		flex-direction: row;

		img {
			max-width: 50%;
		}
	}

	[data-questioninput] {
		margin-left: 15px;
		max-width: 100%;
		min-width: 10%;
	}
}

[data-sr-type="regular"],
[data-sr-type="only-text"] {
	[data-has-no-image][data-no-radio] {
		&[data-answer-type="Checkbox"],
		&[data-answer-type="Radiobutton"] {
			border-left: 2px dotted rgba(209, 209, 209, 1);
			padding-left: 15px;

			&[data-answer-checked] {
				border-left: 3px solid var(--primary-color);
			}
		}
	}
}

[data-answer-checked="true"][data-no-radio="true"] img {
	outline: 1.31px solid var(--primary-color);
	box-shadow: 0 0 5px #000;
    border-radius: 5px;
}

img {
	margin: 7px;
	transition: 1.2s opacity;
}

[data-no-radio] {
	[data-answer-radio] {
		border: 0;
		clip: rect(0 0 0 0);
		height: 1px;
		margin: -1px;
		overflow: hidden;
		padding: 0;
		position: absolute;
		width: 1px;
	}

    [data-answer-input] {
        padding-left: 15px;
    }
}

[data-coupled-to-radio] {
	cursor: pointer;
	transition: 0.4s ease-out;
}

[selected] {
	outline: 1px solid #333;
}

[response] {
	position: relative;  
}

[data-answer-radio] {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 15px;
	align-self: flex-start;
}

[data-answer-type="Radiobutton"],
[data-answer-type="Checkbox"] {
	[data-answer-radio] {
		align-self: normal;
	}
	[data-answer-input] {
		margin-top: 0px;
	}
}

[data-columns="1"] [data-answer-type="Description"]:not([data-no-radio="true"]) {
	padding-left: 45px;
}

[data-answer-input] {
	display: flex;
	align-items: flex-start;
	flex-direction: column;
	max-width: 100%;
	min-width: 10%;
	width: 100%;
	padding: 0 3px 0 0;

	> *:nth-child(2) {
		margin-top: 7px;
	}

    [data-tooltip] {
        margin-top: 0;
    }

    label {
        padding: 16px 20px 16px 0;
    }
}

[data-answer-type="Description"] [data-answer-input] label {
    padding-inline-start: 20px;
}

[question] [data-answer][data-answer-roundbox][data-answer-type="Description"] {
    border: none;
    cursor: default;
    [data-answer-input] label {
        cursor: default;
    }
    &:hover {
        background: none;
    }
}

[data-answer-input][disabled] {
	cursor: default;
}

[data-answer] {
	[data-media-position="below"] {
		flex-direction: column-reverse;
	}
}

[question] [data-question-main] {
	justify-content: flex-start;
    flex-wrap: nowrap;

	&[data-sr-type="only-images"] {
		flex-direction: row;
		justify-content: center;
        flex-wrap: wrap;
        padding-block-end: 5px;

		img {
			margin-bottom: 0;
		}
	}
}

@media only screen and (min-width: 961px) {
	[question] [data-question-main] {
		&[data-columns] {
			display: grid;
			overflow: hidden;
			grid-gap: 15px;
            > [data-answer] {
                min-width: 0;
            }
        }     

		&[data-columns="2"] {
            grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		&[data-columns="3"] {
            grid-template-columns: repeat(3, minmax(0, 1fr));
		}

		&[data-columns="4"] {
            grid-template-columns: repeat(4, minmax(0, 1fr));
		}

		&[data-columns="5"] {
            grid-template-columns: repeat(5, minmax(0, 1fr));
		}

		&[data-columns="6"] {
            grid-template-columns: repeat(6, minmax(0, 1fr));
		}

		&[data-columns="7"] {
            grid-template-columns: repeat(7, minmax(0, 1fr));
		}

		&[data-columns="8"] {
            grid-template-columns: repeat(8, minmax(0, 1fr));
		}

		&[data-columns="9"] {
            grid-template-columns: repeat(9, minmax(0, 1fr));
		}

		&[data-columns="10"] {
            grid-template-columns: repeat(10, minmax(0, 1fr));
		}

		&[data-columns="11"] {
            grid-template-columns: repeat(11, minmax(0, 1fr));
		}

		&[data-columns="12"] {
            grid-template-columns: repeat(12, minmax(0, 1fr));
		}

        &[data-columns="10"],
        &[data-columns="11"],
        &[data-columns="12"] {
            [data-answer-roundbox] {
                margin: 0;

                [data-answer-radio] {
                    margin-right: 6px;
                }

                [data-answer-radio] [data-checkboxradio] {
                    padding: 10px 0 10px 10px;
                }

                [data-answer-input] {
                    padding: 0 5px 0 0;
                }

                [data-answer-input] label {
                    padding: 10px 4px 10px 0;
                }

                [response-input] > *:nth-child(2) {
                    margin: 8px 8px 8px 0;
                }
            }
        }


		/* This is used by the 'column' option to properly organize the answers */
	> div-col-1 {
        grid-column-start: 1
    }

    > div-col-1:first-of-type {
        grid-row-start: 1
    }

    > div-col-1:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-1:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-1:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-1:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-1:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-1:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-1:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-1:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-1:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-1:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-1:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-1:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-1:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-1:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-1:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-1:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-1:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-1:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-1:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-1:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-1:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-1:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-1:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-1:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-1:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-1:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-1:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-1:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-1:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-1:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-1:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-1:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-1:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-1:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-1:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-1:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-1:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-1:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-1:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-1:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-1:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-1:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-1:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-1:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-1:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-1:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-1:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-1:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-1:nth-of-type(50) {
        grid-row-start: 50
    }

    > div-col-2 {
        grid-column-start: 2
    }

    > div-col-2:first-of-type {
        grid-row-start: 1
    }

    > div-col-2:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-2:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-2:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-2:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-2:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-2:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-2:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-2:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-2:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-2:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-2:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-2:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-2:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-2:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-2:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-2:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-2:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-2:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-2:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-2:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-2:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-2:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-2:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-2:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-2:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-2:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-2:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-2:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-2:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-2:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-2:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-2:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-2:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-2:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-2:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-2:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-2:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-2:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-2:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-2:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-2:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-2:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-2:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-2:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-2:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-2:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-2:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-2:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-2:nth-of-type(50) {
        grid-row-start: 50
    }

    > div-col-3 {
        grid-column-start: 3
    }

    > div-col-3:first-of-type {
        grid-row-start: 1
    }

    > div-col-3:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-3:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-3:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-3:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-3:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-3:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-3:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-3:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-3:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-3:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-3:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-3:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-3:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-3:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-3:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-3:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-3:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-3:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-3:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-3:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-3:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-3:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-3:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-3:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-3:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-3:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-3:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-3:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-3:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-3:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-3:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-3:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-3:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-3:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-3:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-3:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-3:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-3:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-3:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-3:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-3:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-3:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-3:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-3:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-3:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-3:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-3:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-3:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-3:nth-of-type(50) {
        grid-row-start: 50
    }

    > div-col-4 {
        grid-column-start: 4
    }

    > div-col-4:first-of-type {
        grid-row-start: 1
    }

    > div-col-4:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-4:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-4:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-4:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-4:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-4:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-4:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-4:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-4:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-4:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-4:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-4:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-4:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-4:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-4:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-4:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-4:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-4:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-4:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-4:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-4:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-4:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-4:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-4:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-4:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-4:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-4:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-4:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-4:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-4:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-4:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-4:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-4:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-4:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-4:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-4:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-4:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-4:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-4:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-4:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-4:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-4:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-4:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-4:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-4:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-4:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-4:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-4:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-4:nth-of-type(50) {
        grid-row-start: 50
    }

    > div-col-5 {
        grid-column-start: 5
    }

    > div-col-5:first-of-type {
        grid-row-start: 1
    }

    > div-col-5:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-5:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-5:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-5:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-5:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-5:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-5:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-5:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-5:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-5:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-5:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-5:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-5:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-5:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-5:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-5:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-5:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-5:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-5:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-5:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-5:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-5:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-5:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-5:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-5:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-5:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-5:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-5:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-5:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-5:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-5:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-5:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-5:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-5:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-5:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-5:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-5:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-5:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-5:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-5:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-5:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-5:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-5:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-5:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-5:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-5:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-5:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-5:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-5:nth-of-type(50) {
        grid-row-start: 50
    }

    > div-col-6 {
        grid-column-start: 6
    }

    > div-col-6:first-of-type {
        grid-row-start: 1
    }

    > div-col-6:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-6:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-6:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-6:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-6:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-6:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-6:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-6:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-6:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-6:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-6:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-6:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-6:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-6:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-6:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-6:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-6:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-6:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-6:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-6:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-6:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-6:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-6:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-6:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-6:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-6:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-6:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-6:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-6:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-6:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-6:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-6:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-6:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-6:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-6:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-6:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-6:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-6:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-6:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-6:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-6:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-6:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-6:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-6:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-6:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-6:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-6:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-6:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-6:nth-of-type(50) {
        grid-row-start: 50
    }

    > div-col-7 {
        grid-column-start: 7
    }

    > div-col-7:first-of-type {
        grid-row-start: 1
    }

    > div-col-7:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-7:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-7:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-7:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-7:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-7:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-7:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-7:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-7:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-7:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-7:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-7:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-7:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-7:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-7:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-7:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-7:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-7:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-7:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-7:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-7:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-7:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-7:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-7:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-7:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-7:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-7:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-7:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-7:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-7:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-7:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-7:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-7:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-7:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-7:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-7:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-7:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-7:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-7:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-7:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-7:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-7:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-7:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-7:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-7:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-7:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-7:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-7:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-7:nth-of-type(50) {
        grid-row-start: 50
    }

    > div-col-8 {
        grid-column-start: 8
    }

    > div-col-8:first-of-type {
        grid-row-start: 1
    }

    > div-col-8:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-8:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-8:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-8:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-8:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-8:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-8:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-8:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-8:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-8:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-8:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-8:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-8:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-8:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-8:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-8:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-8:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-8:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-8:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-8:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-8:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-8:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-8:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-8:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-8:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-8:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-8:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-8:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-8:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-8:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-8:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-8:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-8:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-8:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-8:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-8:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-8:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-8:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-8:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-8:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-8:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-8:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-8:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-8:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-8:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-8:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-8:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-8:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-8:nth-of-type(50) {
        grid-row-start: 50
    }

    > div-col-9 {
        grid-column-start: 9
    }

    > div-col-9:first-of-type {
        grid-row-start: 1
    }

    > div-col-9:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-9:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-9:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-9:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-9:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-9:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-9:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-9:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-9:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-9:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-9:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-9:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-9:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-9:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-9:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-9:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-9:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-9:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-9:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-9:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-9:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-9:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-9:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-9:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-9:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-9:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-9:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-9:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-9:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-9:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-9:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-9:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-9:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-9:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-9:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-9:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-9:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-9:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-9:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-9:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-9:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-9:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-9:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-9:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-9:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-9:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-9:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-9:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-9:nth-of-type(50) {
        grid-row-start: 50
    }

    > div-col-10 {
        grid-column-start: 10
    }

    > div-col-10:first-of-type {
        grid-row-start: 1
    }

    > div-col-10:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-10:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-10:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-10:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-10:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-10:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-10:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-10:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-10:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-10:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-10:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-10:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-10:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-10:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-10:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-10:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-10:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-10:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-10:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-10:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-10:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-10:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-10:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-10:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-10:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-10:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-10:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-10:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-10:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-10:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-10:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-10:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-10:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-10:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-10:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-10:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-10:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-10:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-10:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-10:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-10:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-10:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-10:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-10:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-10:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-10:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-10:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-10:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-10:nth-of-type(50) {
        grid-row-start: 50
    }

    > div-col-11 {
        grid-column-start: 11
    }

    > div-col-11:first-of-type {
        grid-row-start: 1
    }

    > div-col-11:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-11:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-11:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-11:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-11:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-11:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-11:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-11:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-11:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-11:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-11:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-11:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-11:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-11:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-11:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-11:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-11:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-11:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-11:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-11:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-11:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-11:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-11:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-11:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-11:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-11:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-11:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-11:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-11:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-11:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-11:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-11:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-11:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-11:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-11:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-11:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-11:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-11:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-11:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-11:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-11:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-11:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-11:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-11:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-11:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-11:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-11:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-11:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-11:nth-of-type(50) {
        grid-row-start: 50
    }

    > div-col-12 {
        grid-column-start: 12
    }

    > div-col-12:first-of-type {
        grid-row-start: 1
    }

    > div-col-12:nth-of-type(2) {
        grid-row-start: 2
    }

    > div-col-12:nth-of-type(3) {
        grid-row-start: 3
    }

    > div-col-12:nth-of-type(4) {
        grid-row-start: 4
    }

    > div-col-12:nth-of-type(5) {
        grid-row-start: 5
    }

    > div-col-12:nth-of-type(6) {
        grid-row-start: 6
    }

    > div-col-12:nth-of-type(7) {
        grid-row-start: 7
    }

    > div-col-12:nth-of-type(8) {
        grid-row-start: 8
    }

    > div-col-12:nth-of-type(9) {
        grid-row-start: 9
    }

    > div-col-12:nth-of-type(10) {
        grid-row-start: 10
    }

    > div-col-12:nth-of-type(11) {
        grid-row-start: 11
    }

    > div-col-12:nth-of-type(12) {
        grid-row-start: 12
    }

    > div-col-12:nth-of-type(13) {
        grid-row-start: 13
    }

    > div-col-12:nth-of-type(14) {
        grid-row-start: 14
    }

    > div-col-12:nth-of-type(15) {
        grid-row-start: 15
    }

    > div-col-12:nth-of-type(16) {
        grid-row-start: 16
    }

    > div-col-12:nth-of-type(17) {
        grid-row-start: 17
    }

    > div-col-12:nth-of-type(18) {
        grid-row-start: 18
    }

    > div-col-12:nth-of-type(19) {
        grid-row-start: 19
    }

    > div-col-12:nth-of-type(20) {
        grid-row-start: 20
    }

    > div-col-12:nth-of-type(21) {
        grid-row-start: 21
    }

    > div-col-12:nth-of-type(22) {
        grid-row-start: 22
    }

    > div-col-12:nth-of-type(23) {
        grid-row-start: 23
    }

    > div-col-12:nth-of-type(24) {
        grid-row-start: 24
    }

    > div-col-12:nth-of-type(25) {
        grid-row-start: 25
    }

    > div-col-12:nth-of-type(26) {
        grid-row-start: 26
    }

    > div-col-12:nth-of-type(27) {
        grid-row-start: 27
    }

    > div-col-12:nth-of-type(28) {
        grid-row-start: 28
    }

    > div-col-12:nth-of-type(29) {
        grid-row-start: 29
    }

    > div-col-12:nth-of-type(30) {
        grid-row-start: 30
    }

    > div-col-12:nth-of-type(31) {
        grid-row-start: 31
    }

    > div-col-12:nth-of-type(32) {
        grid-row-start: 32
    }

    > div-col-12:nth-of-type(33) {
        grid-row-start: 33
    }

    > div-col-12:nth-of-type(34) {
        grid-row-start: 34
    }

    > div-col-12:nth-of-type(35) {
        grid-row-start: 35
    }

    > div-col-12:nth-of-type(36) {
        grid-row-start: 36
    }

    > div-col-12:nth-of-type(37) {
        grid-row-start: 37
    }

    > div-col-12:nth-of-type(38) {
        grid-row-start: 38
    }

    > div-col-12:nth-of-type(39) {
        grid-row-start: 39
    }

    > div-col-12:nth-of-type(40) {
        grid-row-start: 40
    }

    > div-col-12:nth-of-type(41) {
        grid-row-start: 41
    }

    > div-col-12:nth-of-type(42) {
        grid-row-start: 42
    }

    > div-col-12:nth-of-type(43) {
        grid-row-start: 43
    }

    > div-col-12:nth-of-type(44) {
        grid-row-start: 44
    }

    > div-col-12:nth-of-type(45) {
        grid-row-start: 45
    }

    > div-col-12:nth-of-type(46) {
        grid-row-start: 46
    }

    > div-col-12:nth-of-type(47) {
        grid-row-start: 47
    }

    > div-col-12:nth-of-type(48) {
        grid-row-start: 48
    }

    > div-col-12:nth-of-type(49) {
        grid-row-start: 49
    }

    > div-col-12:nth-of-type(50) {
        grid-row-start: 50
    }
	}
}

@media only screen and (max-width: 550px) {
	[data-sr-type="only-images"] [data-answer] {
		img {
			margin: 0 2px;
		}

        [data-answer-input] {
            padding-left: 5px;
        }
	}

	[data-answer-input] {
		margin-top: 12px;
		> *:nth-child(2) {
			margin-top: 15px;
		}
	}

	[data-answer-type="Description"]:not([data-no-radio="true"]) {
		padding-left: 55px;
	}

    [data-answer-type="Description"] [data-answer-input] {
        margin-top: 0;
    }

	img {
		margin: 5px 0;
	}
}
</style>